/**
 * A lifecycle status, rendered consistently everywhere.
 *
 * Every status string in the system comes from the CHECK constraints mirrored in
 * `@parinaam-vms/shared`, so one component can colour all six lifecycles. Staff
 * then learn one visual language: amber is waiting on someone, green is settled,
 * red is refused.
 *
 * `PENDING` deliberately shares its colour across attendance and volunteers — to a
 * member of staff they mean the same thing, whatever table they live in.
 */

import styles from './StatusBadge.module.css';

type Tone = 'pending' | 'approved' | 'rejected' | 'neutral';

const TONE_BY_STATUS: Readonly<Record<string, Tone>> = {
  // Waiting on a human
  PENDING: 'pending',
  SUBMITTED: 'pending',
  UNDER_REVIEW: 'pending',
  DRAFT: 'neutral',

  // Settled, and good
  APPROVED: 'approved',
  ACTIVE: 'approved',
  SCHEDULED: 'approved',
  ASSIGNED: 'approved',
  COMPLETED: 'approved',

  // Settled, and not
  REJECTED: 'rejected',
  CANCELLED: 'rejected',
  INACTIVE: 'neutral',

  // Deleted, and permanently. Red rather than grey: a deleted program still
  // appears in lists and its badge is the only thing saying its numbers no
  // longer count, so it has to carry more weight than "inactive".
  DELETED: 'rejected',
};

/** `UNDER_REVIEW` -> `Under review`. Staff never see a database value verbatim (§8.4.4). */
function humanise(status: string): string {
  const lower = status.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export interface StatusBadgeProps {
  status: string;
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const tone = TONE_BY_STATUS[status] ?? 'neutral';
  return <span className={`${styles.badge} ${styles[tone]}`}>{humanise(status)}</span>;
}
